
import { useMemo, useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase/firebase';
import { EventWithId } from './useEvents';

interface UseEventFiltersProps {
    events: EventWithId[];
    searchTerm: string;
    filterStatus: 'all' | 'active' | 'closed';
    sortBy: 'date' | 'name' | 'total';
    filterRole?: 'all' | 'owner' | 'joined';
    currentUserId?: string;
}

export const useEventFilters = ({ events, searchTerm, filterStatus, sortBy, filterRole = 'all', currentUserId }: UseEventFiltersProps) => {
    const [eventStatusMap, setEventStatusMap] = useState<{ [eventId: string]: boolean }>({});

    // Listen to eventList for status changes
    useEffect(() => {
        if (events.length === 0) return;

        const eventListRef = ref(database, 'eventList');
        const unsubscribe = onValue(eventListRef, (snapshot) => {
            const data = snapshot.val();
            setEventStatusMap(data || {});
        });

        return () => unsubscribe();
    }, [events.length]);

    const filteredAndSortedEvents = useMemo(() => {
        let filtered = events;

        // ✅ Filter by search term (only event name and description)
        if (searchTerm) {
            filtered = filtered.filter(event =>
                event.meta.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                event.meta.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by status using eventList data
        if (filterStatus !== 'all') {
            filtered = filtered.filter(event => {
                const isActive = eventStatusMap[event.id];
                const isClosed = !isActive;
                return filterStatus === 'active' ? !isClosed : isClosed;
            });
        }

        // Filter by role (Owner vs Joined)
        if (filterRole !== 'all' && currentUserId) {
            filtered = filtered.filter(event => {
                const isOwner = event.meta.createdBy === currentUserId;
                return filterRole === 'owner' ? isOwner : !isOwner;
            });
        }

        // Sort events
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.meta.name.localeCompare(b.meta.name);
                case 'total': {
                    const aTotal = Object.values(a.expenses || {}).reduce((sum, expense) => sum + expense.amount, 0);
                    const bTotal = Object.values(b.expenses || {}).reduce((sum, expense) => sum + expense.amount, 0);
                    return bTotal - aTotal;
                }
                case 'date':
                default:
                    return b.meta.createdAt - a.meta.createdAt;
            }
        });

        return filtered;
    }, [events, searchTerm, filterStatus, sortBy, filterRole, currentUserId, eventStatusMap]);

    return { filteredAndSortedEvents, eventStatusMap };
};